// Motor metrológico de calibraciones (funciones puras, sin dependencias).
//
// ⚠ Este archivo existe en dos lugares con contenido IDÉNTICO:
//    server/src/utils/calibCalculo.util.js   (fuente de verdad: recálculo y validación)
//    client/src/utils/calibCalculo.js        (vista previa en tiempo real, también sin conexión)
//  El test calibCalculo.util.test.js falla si las dos copias difieren.
//
// Convenciones (VIM / GUM):
//  - Error del patrón (certificado):   E_p = I_p − V_conv
//  - Corrección del patrón:             C_p = −E_p
//  - Valor de referencia corregido:     V_ref,c = V_p + C_p(V_p)
//  - Error del equipo bajo calibración: E = I − V_ref,c
//  - Incertidumbres estándar independientes combinadas por suma cuadrática;
//    U = k·u_c, con k fijo o por Welch–Satterthwaite (t de Student al 95,45 %).
//
// Métodos de medición por variable:
//  - "ebc_mide":   el equipo bajo calibración (EBC) indica y el patrón genera la
//                  referencia. valorMedido = indicación del EBC. Si la variable
//                  captura la lectura del patrón (valorPatron), la referencia es
//                  esa lectura corregida; si no, el nominal corregido.
//  - "ebc_genera": el EBC genera el valor nominal (p. ej. energía de un
//                  desfibrilador) y el patrón lo mide. valorMedido = lectura del
//                  patrón; I = nominal.

export const MOTOR_VERSION = "1.0.0";

const EPS = 1e-9;

// ─── Utilidades numéricas ────────────────────────────────────────────────
// Valor numérico finito o null. "" / null / undefined / texto → null (nunca 0).
export const num = (v) => {
  if (v === null || v === undefined) return null;
  if (typeof v === "string" && v.trim() === "") return null;
  if (typeof v === "boolean") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const idStr = (v) => (v && v._id ? String(v._id) : v ? String(v) : "");

export const media = (xs) =>
  xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;

// Desviación estándar muestral (n − 1). null si n < 2.
export const desviacionEstandar = (xs) => {
  if (xs.length < 2) return null;
  const m = media(xs);
  const ss = xs.reduce((a, x) => a + (x - m) ** 2, 0);
  return Math.sqrt(ss / (xs.length - 1));
};

// Redondeo decimal sin errores de representación binaria (1.005 → 1.01)
export const redondear = (x, decimales) => {
  if (x === null || x === undefined || !Number.isFinite(x)) return null;
  const s = x < 0 ? -1 : 1;
  const r = Number(`${Math.round(Number(`${Math.abs(x)}e${decimales}`))}e-${decimales}`);
  return r === 0 ? 0 : s * r;
};

// Decimales de presentación: todos los resultados se muestran a 2 decimales
// (los valores almacenados y los cálculos intermedios no se redondean).
export const DECIMALES_PRESENTACION = 2;
export const decimalesPresentacion = () => DECIMALES_PRESENTACION;

// Texto de presentación de un valor (el valor almacenado no se altera)
export const formatear = (x, decimales) => {
  const n = num(x);
  if (n === null) return "—";
  if (decimales === null || decimales === undefined) {
    return String(Number(n.toPrecision(6)));
  }
  return redondear(n, decimales).toFixed(decimales);
};

// ─── t de Student para p = 95,45 % (GUM, tabla G.2) ──────────────────────
const TABLA_T = [
  [1, 13.97], [2, 4.53], [3, 3.31], [4, 2.87], [5, 2.65], [6, 2.52],
  [7, 2.43], [8, 2.37], [9, 2.32], [10, 2.28], [11, 2.25], [12, 2.23],
  [13, 2.21], [14, 2.2], [15, 2.18], [16, 2.17], [17, 2.16], [18, 2.15],
  [19, 2.14], [20, 2.13], [25, 2.11], [30, 2.09], [35, 2.07], [40, 2.06],
  [45, 2.06], [50, 2.05], [100, 2.025],
];

// k para ν grados de libertad: ν se trunca y se toma la fila tabulada inferior
// (más conservadora). ν = Infinity → 2.
export const kStudent = (nu) => {
  if (nu === null || nu === undefined || !(nu >= 1)) return null;
  if (!Number.isFinite(nu)) return 2;
  const v = Math.floor(nu);
  let k = 2;
  for (const [g, t] of TABLA_T) {
    if (g <= v) k = t;
    else break;
  }
  return v > 100 ? 2 : k;
};

// Grados de libertad que corresponden al k declarado por un certificado:
// el mayor ν cuyo t ≥ k (conservador). k ≤ 2 → ∞.
export const gradosLibertadDeK = (k) => {
  const kk = num(k);
  if (kk === null || kk <= 2 + EPS) return Infinity;
  let nu = 1;
  for (const [g, t] of TABLA_T) if (t >= kk - EPS) nu = g;
  return nu;
};

// ─── Patrón: corrección e incertidumbre en un valor x ────────────────────
export const METODOS_CORRECCION = ["ninguna", "puntual", "interpolacion", "regresion"];

const puntosPatron = (patron) =>
  (patron?.puntos || [])
    .map((p) => ({
      valor: num(p.valor),
      error: num(p.error),
      U: num(p.incertidumbre),
      k: num(p.k),
    }))
    .filter((p) => p.valor !== null)
    .sort((a, b) => a.valor - b.valor);

const uDe = (p) => (p.U !== null && p.k !== null && p.k > 0 ? p.U / p.k : null);

// Mínimos cuadrados de la corrección (C = −E) sobre el valor
export const regresionLineal = (xs, ys) => {
  const n = xs.length;
  if (n < 2) return null;
  const mx = media(xs);
  const my = media(ys);
  const sxx = xs.reduce((a, x) => a + (x - mx) ** 2, 0);
  if (sxx === 0) return null;
  const sxy = xs.reduce((a, x, i) => a + (x - mx) * (ys[i] - my), 0);
  const m = sxy / sxx;
  const b = my - m * mx;
  const ss = xs.reduce((a, x, i) => a + (ys[i] - (m * x + b)) ** 2, 0);
  // Incertidumbre por interpolación (hoja de cálculo del laboratorio):
  // √(Σ diferencias²) / (N° puntos de calibración − puntos de la recta)
  const sEst = n > 2 ? Math.sqrt(ss) / (n - 2) : null;
  return { m, b, sEst, n };
};

const mismoValor = (a, b) => Math.abs(a - b) <= EPS * Math.max(1, Math.abs(b));

// Devuelve { correccion, uPat, kPat, uInterp, nuInterp, errores[], advertencias[] }
// x: valor donde se evalúa la corrección; xRef: valor nominal del punto de
// calibración, cuyo punto del certificado da la incertidumbre del patrón (por defecto x)
export const evaluarPatron = (patron, x, xRef = x) => {
  const out = {
    correccion: 0,
    uPat: null,
    kPat: null,
    uInterp: null,
    nuInterp: null,
    errores: [],
    advertencias: [],
  };
  if (!patron) return out;
  const pts = puntosPatron(patron);
  const metodo = METODOS_CORRECCION.includes(patron.metodoCorreccion)
    ? patron.metodoCorreccion
    : "ninguna";
  const nombre = patron.nombre || "patrón";

  if (!pts.length) {
    out.advertencias.push(
      `${nombre}: sin puntos de calibración registrados; no se aplica corrección ni se conoce su incertidumbre`,
    );
    return out;
  }
  if (x === null) return out;

  // Fuera del intervalo calibrado no se extrapola: se usan los datos del
  // punto extremo más cercano (regresión sí evalúa la recta en x)
  const min = pts[0].valor;
  const max = pts[pts.length - 1].valor;
  let xc = x;
  if (x < min - EPS || x > max + EPS) {
    xc = x < min ? min : max;
    out.advertencias.push(
      `${nombre}: el valor ${redondear(x, 6)} está fuera del intervalo calibrado del patrón (${min} a ${max}); se usan los datos del punto ${xc}`,
    );
  }

  // Puntos que encierran x (o el punto exacto)
  const exacto = pts.find((p) => Math.abs(p.valor - xc) <= EPS * Math.max(1, Math.abs(xc)));
  const sup = pts.find((p) => p.valor >= xc);
  const inf = [...pts].reverse().find((p) => p.valor <= xc);
  const vecinos = exacto ? [exacto] : [inf, sup].filter(Boolean);

  // Incertidumbre del patrón: U/k del punto del certificado igual al nominal,
  // sin importar el valor medido y sin aproximar a otro punto
  const ref = num(xRef) ?? x;
  const delPunto = pts.find((p) => mismoValor(p.valor, ref));
  if (!delPunto) {
    out.advertencias.push(
      `${nombre}: el punto ${redondear(ref, 6)} no está en el certificado del patrón; no se conoce su incertidumbre`,
    );
  } else if (uDe(delPunto) === null) {
    out.advertencias.push(`${nombre}: falta U o k en el certificado para el punto ${delPunto.valor}`);
  } else {
    out.uPat = uDe(delPunto);
    out.kPat = delPunto.k;
  }
  // Aviso si lo medido queda más cerca de otro punto del certificado (la U/k sigue siendo la del nominal)
  if (delPunto && x !== null) {
    const cercano = pts.reduce((a, p) =>
      Math.abs(p.valor - x) < Math.abs(a.valor - x) - EPS ? p : a,
    );
    if (!mismoValor(cercano.valor, delPunto.valor)) {
      out.advertencias.push(
        `${nombre}: el resultado medido (${redondear(x, 6)}) está más cerca del punto ${cercano.valor} del certificado; se usa la U/k del punto ${delPunto.valor}`,
      );
    }
  }

  const corr = (p) => (p.error === null ? null : -p.error);

  if (metodo === "ninguna") {
    out.correccion = 0;
  } else if (metodo === "puntual") {
    if (!exacto) {
      // Sin punto exacto: promedio del error de los dos puntos que encierran x
      const prom = (vals) => (vals.some((v) => v === null) ? null : media(vals));
      const error = prom(vecinos.map((p) => p.error));
      if (error === null) {
        out.errores.push(`${nombre}: faltan errores del certificado para promediar en ${x}`);
      } else out.correccion = -error;
    } else if (corr(exacto) === null) {
      out.errores.push(`${nombre}: el punto ${x} del patrón no tiene error registrado`);
    } else out.correccion = corr(exacto);
  } else if (metodo === "interpolacion") {
    if (vecinos.some((p) => corr(p) === null)) {
      out.errores.push(`${nombre}: faltan errores del certificado para interpolar en ${x}`);
    } else if (vecinos.length === 1 || inf.valor === sup.valor) {
      out.correccion = corr(vecinos[0]);
    } else {
      const t = (xc - inf.valor) / (sup.valor - inf.valor);
      out.correccion = corr(inf) + t * (corr(sup) - corr(inf));
    }
  } else if (metodo === "regresion") {
    const conError = pts.filter((p) => p.error !== null);
    const reg = regresionLineal(
      conError.map((p) => p.valor),
      conError.map((p) => -p.error),
    );
    if (!reg) {
      out.errores.push(`${nombre}: se requieren al menos 2 puntos con error para la regresión`);
    } else {
      out.correccion = reg.m * x + reg.b;
      out.uInterp = reg.sEst;
      out.nuInterp = reg.n > 2 ? reg.n - 2 : null;
      if (reg.sEst === null) {
        out.advertencias.push(
          `${nombre}: con 2 puntos no puede estimarse la incertidumbre del ajuste`,
        );
      }
    }
  }
  return out;
};

// Estado del patrón en una fecha (no depende del campo `estado` almacenado)
export const estadoPatronEn = (patron, fecha = new Date()) => {
  if (!patron?.fechaVencimiento) return "pendiente";
  const dia = (d) => new Date(d).toISOString().slice(0, 10);
  if (patron.fechaCalibracion && dia(patron.fechaCalibracion) > dia(fecha)) {
    return "pendiente";
  }
  return dia(patron.fechaVencimiento) >= dia(fecha) ? "vigente" : "vencido";
};

// ─── Tolerancia y decisión ───────────────────────────────────────────────
export const REGLAS_TOLERANCIA = ["mayor", "menor", "suma"];
export const REGLAS_DECISION = ["simple", "banda_guarda"];

const tolNum = (v) => {
  const n = num(v);
  return n === null ? null : Math.abs(n);
};

// Tolerancias efectivas de un punto: si el punto define alguna, reemplazan las de la variable
export const toleranciasDePunto = (variable = {}, punto = {}) => {
  const absP = tolNum(punto?.toleranciaAbsoluta);
  const pctP = tolNum(punto?.toleranciaPorcentual);
  if (absP !== null || pctP !== null) return { abs: absP, pct: pctP, propia: true };
  return {
    abs: tolNum(variable?.toleranciaAbsoluta),
    pct: tolNum(variable?.toleranciaPorcentual),
    propia: false,
  };
};

// Desviación máxima permitida. El porcentaje se toma sobre el valor nominal.
// Devuelve { delta, definida, motivo }: definida=true y delta=null → no evaluable.
export const toleranciaPermitida = (nominal, abs, pct, regla = "mayor") => {
  if (abs === null && pct === null) return { delta: null, definida: false };
  const partes = [];
  if (abs !== null) partes.push(abs);
  if (pct !== null) {
    if (nominal === null || nominal === 0) {
      if (abs === null) {
        return {
          delta: null,
          definida: true,
          motivo:
            "la tolerancia porcentual no es aplicable a un valor nominal 0 o vacío; defina una tolerancia absoluta para el punto",
        };
      }
    } else partes.push(Math.abs((nominal * pct) / 100));
  }
  const r = REGLAS_TOLERANCIA.includes(regla) ? regla : "mayor";
  const delta =
    r === "suma"
      ? partes.reduce((a, b) => a + b, 0)
      : r === "menor"
        ? Math.min(...partes)
        : Math.max(...partes);
  return { delta, definida: true };
};

// decision: conforme | no_conforme | indeterminado | sin_criterio | pendiente
export const decidir = ({ E, U, delta, definida, reglaDecision = "simple" }) => {
  if (!definida) return "sin_criterio";
  if (E === null || delta === null) return "pendiente";
  const e = Math.abs(E);
  if (reglaDecision === "banda_guarda") {
    if (U === null) return "pendiente";
    if (e + U <= delta + EPS) return "conforme";
    if (e - U > delta + EPS) return "no_conforme";
    return "indeterminado";
  }
  return e <= delta + EPS ? "conforme" : "no_conforme";
};

// ─── Cálculo de un punto ─────────────────────────────────────────────────
export const METODOS_MEDICION = ["ebc_mide", "ebc_genera"];

export const calcularPunto = ({
  variable = {},
  punto = {},
  medicion = {},
  patron = null,
  config = {},
}) => {
  const metodo = METODOS_MEDICION.includes(variable.metodo) ? variable.metodo : "ebc_mide";
  const capturaPatron = metodo === "ebc_mide" && variable.capturaPatron === true;
  const nominal = num(punto?.valor ?? medicion.puntoReferencia);
  const reps = medicion.repeticiones || [];
  const errores = [];
  const advertencias = [];

  const lecturas = reps.map((r) => num(r?.valorMedido)).filter((v) => v !== null);

  const vacio = {
    n: lecturas.length,
    promedio: lecturas.length ? media(lecturas) : null,
    promedioPatron: null,
    valorReferencia: null,
    correccionPatron: null,
    errorAbsoluto: null,
    errorRelativo: null,
    desviacionEstandar: desviacionEstandar(lecturas),
    incertidumbre: null,
    incertidumbreExpandida: null,
    toleranciaAplicada: null,
    decision: "pendiente",
    cumple: null,
  };

  // Lecturas emparejadas (EBC y patrón) cuando se captura el patrón
  let pares = [];
  if (capturaPatron) {
    pares = reps
      .map((r) => [num(r?.valorMedido), num(r?.valorPatron)])
      .filter(([a, b]) => a !== null && b !== null);
  }

  let I = null; // indicación del EBC
  let Vp = null; // valor del patrón antes de corregir
  let dispersion = []; // datos cuya dispersión da la repetibilidad
  if (metodo === "ebc_genera") {
    I = nominal;
    Vp = media(lecturas);
    dispersion = lecturas;
  } else if (capturaPatron) {
    I = pares.length ? media(pares.map(([a]) => a)) : null;
    Vp = pares.length ? media(pares.map(([, b]) => b)) : null;
    dispersion = pares.map(([a, b]) => a - b);
  } else {
    I = media(lecturas);
    Vp = nominal;
    dispersion = lecturas;
  }

  if (I === null || Vp === null) {
    const tol = toleranciasDePunto(variable, punto);
    const t = toleranciaPermitida(nominal, tol.abs, tol.pct, config.reglaTolerancia);
    return {
      ...vacio,
      decision: t.definida ? "pendiente" : "sin_criterio",
      errores,
      advertencias,
    };
  }

  const pat = evaluarPatron(patron, Vp, nominal ?? Vp);
  errores.push(...pat.errores);
  advertencias.push(...pat.advertencias);
  const correccion = pat.errores.length ? null : pat.correccion;
  const Vref = correccion === null ? null : Vp + correccion;
  const E = Vref === null ? null : I - Vref;
  const ref = nominal !== null && nominal !== 0 ? nominal : null;
  const errorRelativo = E === null || ref === null ? null : (E / ref) * 100;

  // ── Incertidumbre ──
  let inc = null;
  if (config.incertidumbre?.calcular !== false) {
    const n = dispersion.length;
    const s = desviacionEstandar(dispersion);
    const uRep = s === null ? null : s / Math.sqrt(n);
    if (n < 2) {
      advertencias.push("con menos de 2 repeticiones no se evalúa la repetibilidad");
    }
    const res = num(variable.resolucion);
    const uRes = res !== null && res > 0 ? res / Math.sqrt(12) : null;
    const comps = [
      { u: uRep, nu: n > 1 ? n - 1 : null },
      { u: uRes, nu: Infinity },
      { u: pat.uPat, nu: gradosLibertadDeK(pat.kPat) },
      { u: pat.uInterp, nu: pat.nuInterp },
    ].filter((c) => c.u !== null);
    const uc = Math.sqrt(comps.reduce((a, c) => a + c.u ** 2, 0));
    const sinPatron = patron && pat.uPat === null;
    let k = null;
    let nuEff = null;
    if (uc > 0) {
      const den = comps.reduce(
        (a, c) => a + (c.nu && Number.isFinite(c.nu) ? c.u ** 4 / c.nu : 0),
        0,
      );
      nuEff = den > 0 ? uc ** 4 / den : Infinity;
      k =
        config.incertidumbre?.kModo === "veff"
          ? kStudent(nuEff)
          : num(config.incertidumbre?.kFijo) ?? 2;
    }
    // Sin la incertidumbre del patrón el presupuesto está incompleto: no se declara U
    const completo = !!patron && !sinPatron && uc > 0;
    if (!patron) advertencias.push("sin patrón asignado: no se declara incertidumbre");
    else if (sinPatron) advertencias.push("incertidumbre del patrón desconocida: no se declara U");
    inc = {
      uRep,
      uRes,
      uPat: pat.uPat,
      uInterp: pat.uInterp,
      uc: uc > 0 ? uc : null,
      nuEff: nuEff === Infinity ? null : nuEff,
      k,
      U: completo && k !== null ? k * uc : null,
    };
  }
  const U = inc?.U ?? null;

  // ── Tolerancia y decisión ──
  const tol = toleranciasDePunto(variable, punto);
  const t = toleranciaPermitida(nominal, tol.abs, tol.pct, config.reglaTolerancia);
  if (t.definida && t.delta === null && t.motivo) errores.push(t.motivo);
  const decision = decidir({
    E,
    U,
    delta: t.delta,
    definida: t.definida,
    reglaDecision: config.reglaDecision,
  });
  if (decision === "pendiente" && t.definida && config.reglaDecision === "banda_guarda" && U === null && E !== null) {
    errores.push("la regla de decisión con banda de guarda requiere la incertidumbre expandida");
  }

  return {
    n: dispersion.length,
    promedio: metodo === "ebc_genera" ? Vp : I,
    promedioPatron: capturaPatron || metodo === "ebc_genera" ? Vp : null,
    valorReferencia: Vref,
    correccionPatron: correccion,
    errorAbsoluto: E,
    errorRelativo,
    desviacionEstandar: desviacionEstandar(dispersion),
    incertidumbre: inc,
    incertidumbreExpandida: U,
    toleranciaAplicada: t.delta,
    decision,
    cumple: decision === "conforme" ? true : decision === "no_conforme" ? false : null,
    errores,
    advertencias,
  };
};

// ─── Resultado global ────────────────────────────────────────────────────
export const CRITERIOS_GLOBALES = ["todas_conformes", "mayoria_conforme", "criticas_conformes"];

// puntos: [{ decision, critica }] solo de variables medidas
export const calcularResultadoGlobal = (puntos = [], criterio = "todas_conformes") => {
  if (!puntos.length) return "Pendiente";
  if (puntos.some((p) => p.decision === "pendiente")) return "Pendiente";
  let evaluados = puntos.filter((p) => p.decision !== "sin_criterio");
  if (!evaluados.length) return "Sin criterio";
  if (criterio === "criticas_conformes") {
    const criticos = evaluados.filter((p) => p.critica);
    if (criticos.length) evaluados = criticos;
  }
  if (criterio === "mayoria_conforme") {
    const conformes = evaluados.filter((p) => p.decision === "conforme").length;
    return conformes * 2 > evaluados.length ? "Conforme" : "No conforme";
  }
  if (evaluados.some((p) => p.decision === "no_conforme")) return "No conforme";
  if (evaluados.some((p) => p.decision === "indeterminado")) return "Indeterminado";
  return "Conforme";
};

// ─── Emparejar mediciones guardadas con los puntos de la plantilla ───────
// Clave estable: variable + índice del punto. Las mediciones antiguas (sin
// puntoIndex) se emparejan por valor y orden de aparición, así dos puntos con el
// mismo nominal no se cruzan.
export const emparejarMediciones = (estructura = [], guardadas = []) => {
  const usadas = new Set();
  const resultado = estructura.map((m) => {
    const vid = idStr(m.variableId);
    let idx = guardadas.findIndex(
      (g, i) =>
        !usadas.has(i) &&
        idStr(g.variableId) === vid &&
        g.puntoIndex !== undefined &&
        g.puntoIndex !== null &&
        Number(g.puntoIndex) === Number(m.puntoIndex),
    );
    if (idx === -1) {
      // La primera no usada con el mismo valor respeta el orden de aparición
      const valor = String(num(m.puntoReferencia) ?? m.puntoReferencia ?? "");
      idx = guardadas.findIndex(
        (g, i) =>
          !usadas.has(i) &&
          (g.puntoIndex === undefined || g.puntoIndex === null) &&
          idStr(g.variableId) === vid &&
          String(num(g.puntoReferencia) ?? g.puntoReferencia ?? "") === valor,
      );
    }
    if (idx === -1) return { estructura: m, guardada: null };
    usadas.add(idx);
    return { estructura: m, guardada: guardadas[idx] };
  });
  const huerfanas = guardadas.filter((_, i) => !usadas.has(i));
  return { pares: resultado, huerfanas };
};

// ─── Procesamiento completo de una calibración ───────────────────────────
// plantilla: CalibrationTemplate (o su instantánea); mediciones: de la gestión;
// patrones: documentos de los patrones referenciados; finalizar: aplica
// las validaciones obligatorias de cierre.
export const configDePlantilla = (plantilla = {}) => ({
  reglaTolerancia: plantilla?.reglasAceptacion?.reglaTolerancia || "mayor",
  reglaDecision: plantilla?.reglasAceptacion?.reglaDecision || "simple",
  criterioGlobal: plantilla?.reglasAceptacion?.criterioGlobal || "todas_conformes",
  incertidumbre: {
    calcular: plantilla?.incertidumbre?.calcular !== false,
    kModo: plantilla?.incertidumbre?.kModo === "veff" ? "veff" : "fijo",
    kFijo: num(plantilla?.incertidumbre?.kFijo) ?? 2,
  },
});

const patronDeMedicion = (m) => {
  const ids = (m.repeticiones || []).map((r) => idStr(r?.patronId)).filter(Boolean);
  return ids[0] || "";
};

const norm = (s) => String(s || "").trim().toLowerCase();

// ─── Variables de medición del patrón ────────────────────────────────────
// Un patrón puede medir varias magnitudes: cada una tiene sus propios puntos
// de certificado en patron.variablesMedicion, compartidos por las plantillas.
export const variableDePatron = (patron, varId) =>
  (patron?.variablesMedicion || []).find((v) => idStr(v._id) === idStr(varId)) || null;

// Patrón "efectivo" para el cálculo: vigencia del patrón + datos metrológicos
// de su variable (mismo formato que consume evaluarPatron/calcularPunto).
export const patronEfectivo = (patron, variablePatron) => ({
  _id: patron._id,
  nombre: variablePatron?.nombre ? `${patron.nombre} · ${variablePatron.nombre}` : patron.nombre,
  numeroCertificado: patron.numeroCertificado,
  fechaCalibracion: patron.fechaCalibracion,
  fechaVencimiento: patron.fechaVencimiento,
  magnitud: variablePatron?.magnitud || "",
  unidad: variablePatron?.unidad || "",
  rangoMin: variablePatron?.rangoMin ?? null,
  rangoMax: variablePatron?.rangoMax ?? null,
  resolucion: variablePatron?.resolucion ?? null,
  metodoCorreccion: variablePatron?.metodoCorreccion || "ninguna",
  puntos: variablePatron?.puntos || [],
  certificadoDatos: variablePatron?.certificado || "",
});

const vinculada = (variable) => !!(idStr(variable?.patron) && idStr(variable?.patronVariable));

// Ids (ordenados, únicos) de los patrones vinculados a las variables de la plantilla
export const patronesDePlantilla = (plantilla) =>
  [...new Set((plantilla?.variables || []).filter(vinculada).map((v) => idStr(v.patron)))].sort();

// Plantillas utilizables con los patrones seleccionados: sus patrones vinculados
// están todos seleccionados. Primero las más específicas; las heredadas (sin
// vínculos) al final.
export const plantillasCompatibles = (plantillas = [], idsSeleccionados = []) => {
  const sel = new Set((idsSeleccionados || []).map(idStr));
  return plantillas
    .map((t) => ({ t, ids: patronesDePlantilla(t) }))
    .filter(({ ids }) => ids.every((id) => sel.has(id)))
    .sort((a, b) => b.ids.length - a.ids.length)
    .map(({ t }) => t);
};

export const procesarCalibracion = ({
  plantilla,
  mediciones = [],
  patrones = [],
  condicionesAmbientales = null,
  fecha = new Date(),
  finalizar = false,
}) => {
  const config = configDePlantilla(plantilla);
  const errores = [];
  const advertencias = [];
  const repsEsperadas = num(plantilla?.repeticionesPorPunto) || 1;
  const patronPorId = new Map(patrones.map((p) => [idStr(p._id), p]));
  const variables = new Map(
    (plantilla?.variables || []).map((v) => [idStr(v._id), v]),
  );
  const conPrefijo = (m, msg) =>
    `${m.nombreVariable || "Variable"} @ ${m.puntoReferencia ?? "—"}: ${msg}`;

  const salida = mediciones.map((m) => {
    const variable = variables.get(idStr(m.variableId));
    if (!variable) {
      if (m.medida !== false) {
        errores.push(conPrefijo(m, "la variable ya no existe en la plantilla"));
      }
      return m;
    }
    const puntos = variable.puntosCalib || [];
    let pIdx = num(m.puntoIndex);
    if (pIdx === null || !puntos[pIdx]) {
      pIdx = puntos.findIndex(
        (p) => String(num(p.valor) ?? p.valor) === String(num(m.puntoReferencia) ?? m.puntoReferencia),
      );
    }
    const punto = pIdx >= 0 ? puntos[pIdx] : { valor: m.puntoReferencia };
    const base = {
      ...m,
      puntoIndex: pIdx >= 0 ? pIdx : m.puntoIndex ?? null,
      puntoReferencia: num(punto.valor) ?? punto.valor ?? null,
      unidad: variable.unidad || m.unidad || "",
    };
    // Variable opcional sin ninguna lectura en el punto: se reporta como no medida
    const sinLecturas = (m.repeticiones || []).every(
      (r) => num(r?.valorMedido) === null && num(r?.valorPatron) === null,
    );
    const opcionalVacia = m.medida !== false && variable.obligatorio === false && sinLecturas;
    if (opcionalVacia) {
      advertencias.push(conPrefijo(m, "variable opcional sin lecturas: se reporta como no medida"));
    }
    if (m.medida === false || opcionalVacia) {
      return {
        ...base,
        medida: false,
        promedio: null,
        errorAbsoluto: null,
        errorRelativo: null,
        incertidumbreExpandida: null,
        decision: null,
        cumple: null,
        advertencias: [],
      };
    }

    // ── Validación de lecturas ──
    const reps = m.repeticiones || [];
    const metodo = METODOS_MEDICION.includes(variable.metodo) ? variable.metodo : "ebc_mide";
    const capturaPatron = metodo === "ebc_mide" && variable.capturaPatron === true;
    const faltantes = reps.filter((r) => num(r?.valorMedido) === null).length +
      Math.max(0, repsEsperadas - reps.length);
    if (finalizar && faltantes > 0) {
      errores.push(conPrefijo(m, `faltan ${faltantes} lectura(s) de ${repsEsperadas}`));
    }
    if (finalizar && capturaPatron) {
      const fp = reps.filter((r) => num(r?.valorPatron) === null).length;
      if (fp > 0) errores.push(conPrefijo(m, `faltan ${fp} lectura(s) del patrón`));
    }
    const rMin = num(variable.rangoMin);
    const rMax = num(variable.rangoMax);
    if (metodo === "ebc_mide") {
      const fuera = reps
        .map((r) => num(r?.valorMedido))
        .filter((v) => v !== null && ((rMin !== null && v < rMin) || (rMax !== null && v > rMax)));
      if (fuera.length) {
        advertencias.push(
          conPrefijo(m, `lecturas fuera del rango del equipo (${rMin ?? "−∞"} a ${rMax ?? "∞"}): ${fuera.join(", ")}`),
        );
      }
    }

    // ── Patrón del punto ──
    // Variable vinculada: el patrón y sus datos los define la plantilla.
    const conVinculo = vinculada(variable);
    const pid = conVinculo ? idStr(variable.patron) : patronDeMedicion(m);
    const docPatron = pid ? patronPorId.get(pid) || null : null;
    let patron = docPatron;
    if (conVinculo && docPatron) {
      const vp = variableDePatron(docPatron, variable.patronVariable);
      if (!vp) {
        errores.push(conPrefijo(m, `la variable de medición del patrón ${docPatron.nombre} ya no existe`));
        patron = null;
      } else {
        patron = patronEfectivo(docPatron, vp);
        if (vp.certificado && docPatron.numeroCertificado && vp.certificado !== docPatron.numeroCertificado) {
          advertencias.push(
            conPrefijo(m, `los datos de ${patron.nombre} se cargaron con el certificado ${vp.certificado}; el certificado actual es ${docPatron.numeroCertificado}`),
          );
        }
      }
    }
    if (finalizar && !pid) errores.push(conPrefijo(m, "no tiene patrón asignado"));
    if (pid && !docPatron) {
      errores.push(conPrefijo(m, conVinculo
        ? "el patrón de la plantilla no está disponible o no pertenece a la empresa"
        : "el patrón asignado no existe o no pertenece a la empresa"));
    }
    if (patron) {
      const estado = estadoPatronEn(patron, fecha);
      if (finalizar && estado !== "vigente") {
        errores.push(conPrefijo(m, `el patrón ${patron.nombre} no está vigente (${estado}) en la fecha de calibración`));
      }
      if (variable.magnitud && patron.magnitud && norm(variable.magnitud) !== norm(patron.magnitud)) {
        errores.push(conPrefijo(m, `el patrón ${patron.nombre} mide "${patron.magnitud}", no "${variable.magnitud}"`));
      }
      if (variable.unidad && patron.unidad && norm(variable.unidad) !== norm(patron.unidad)) {
        errores.push(conPrefijo(m, `unidad del patrón (${patron.unidad}) distinta a la de la variable (${variable.unidad})`));
      }
      const pMin = num(patron.rangoMin);
      const pMax = num(patron.rangoMax);
      const x = num(punto.valor);
      if (x !== null && ((pMin !== null && x < pMin) || (pMax !== null && x > pMax))) {
        errores.push(conPrefijo(m, `el punto está fuera del rango del patrón ${patron.nombre} (${pMin ?? "−∞"} a ${pMax ?? "∞"})`));
      }
    }

    const r = calcularPunto({ variable, punto, medicion: m, patron, config });
    r.errores.forEach((e) => errores.push(conPrefijo(m, e)));
    if (finalizar && r.decision === "pendiente") {
      errores.push(conPrefijo(m, "tiene criterio de aceptación pero no pudo evaluarse"));
    }
    const { errores: _e, ...calculado } = r;
    return {
      ...base,
      ...(conVinculo
        ? { repeticiones: reps.map((rep) => ({ ...rep, patronId: pid })) }
        : {}),
      ...calculado,
      advertencias: r.advertencias,
    };
  });

  const medidas = salida.filter((m) => m.medida !== false && variables.has(idStr(m.variableId)));
  if (finalizar && !medidas.length) errores.push("Debe medirse al menos una variable");

  // ── Condiciones ambientales ──
  const amb = condicionesAmbientales;
  if (finalizar && amb && amb.incluir !== false) {
    const vals = [amb.tempInicio, amb.tempFinal, amb.humInicio, amb.humFinal].map(num);
    if (vals.some((v) => v === null)) {
      errores.push("Registre temperatura y humedad de inicio y final (o excluya las condiciones ambientales)");
    }
    if ([vals[2], vals[3]].some((h) => h !== null && (h < 0 || h > 100))) {
      errores.push("La humedad relativa debe estar entre 0 y 100 %");
    }
    const pa = amb.patron ? patronPorId.get(idStr(amb.patron)) : null;
    if (!amb.patron) errores.push("Seleccione el patrón de las condiciones ambientales");
    else if (!pa) errores.push("El patrón ambiental no existe o no pertenece a la empresa");
    else if (estadoPatronEn(pa, fecha) !== "vigente") {
      errores.push(`El patrón ambiental ${pa.nombre} no está vigente en la fecha de calibración`);
    }
  }

  const criticas = new Set(
    [...variables.values()].filter((v) => v.critica).map((v) => idStr(v._id)),
  );
  const resultadoGlobal = calcularResultadoGlobal(
    medidas.map((m) => ({ decision: m.decision, critica: criticas.has(idStr(m.variableId)) })),
    config.criterioGlobal,
  );
  const conResultado = medidas.filter((m) => m.errorAbsoluto !== null && m.errorAbsoluto !== undefined);
  const tipoDocumento =
    conResultado.length > 0 && conResultado.every((m) => num(m.incertidumbreExpandida) !== null)
      ? "certificado"
      : "informe";

  return {
    mediciones: salida,
    resultadoGlobal,
    tipoDocumento,
    config,
    errores: [...new Set(errores)],
    advertencias: [...new Set(advertencias)],
  };
};
